import {
  commonStyleHandlers,
  Responsive,
  ResponsiveCssProp,
  SapphireCommonStyleProps,
  StyleHandlers,
} from './SapphireStyleProps';
import { FilteredProps, useSapphireStyleProps } from './useSapphireStyleProps';

/**
 * There are some css properties related to text layout which
 * seem like they should be allowed to be customized, but only
 * in those components which are mean to render text.
 *
 * All the usual text-related properties (eg. font-size,
 * line-height, color etc.) are not allowed because those
 * belong to Sapphire itself.
 *
 * https://developer.mozilla.org/en-US/docs/Learn/CSS/Styling_text/Fundamentals#other_properties_worth_looking_at
 */

export type SapphireTextStyleProps = SapphireCommonStyleProps & {
  textAlign?: ResponsiveCssProp<'textAlign'>;
  textIndent?: ResponsiveCssProp<'textIndent'>;
  textOverflow?: ResponsiveCssProp<'textOverflow'>;
  textAlignLast?: ResponsiveCssProp<'textAlignLast'>;
  textJustify?: ResponsiveCssProp<'textJustify'>;
  overflowWrap?: ResponsiveCssProp<'overflowWrap'>;
  hyphens?: ResponsiveCssProp<'hyphens'>;
  whiteSpace?: ResponsiveCssProp<'whiteSpace'>;
  wordBreak?: ResponsiveCssProp<'wordBreak'>;
  overflow?: ResponsiveCssProp<'overflow'>;
  fontStyle?: Responsive<'normal' | 'italic'>;

  /* NOTE:
   *
   * These are the text-layout properties we do not want to support:
   *
   * - line-break: relevant only for asian languages
   * - text-orientation: relevant only for vertical scripts
   * - direction: not relevant. But also it should be defined for the html document itself.
   * - writing-mode: not relevant. But also it should be defined for the html document itself.
   */
};

export const sapphireTextStyleHandlers: StyleHandlers<SapphireTextStyleProps> =
  {
    ...commonStyleHandlers,
    textAlign: 'textAlign',
    textIndent: 'textIndent',
    textOverflow: 'textOverflow',
    textAlignLast: 'textAlignLast',
    textJustify: 'textJustify',
    overflowWrap: 'overflowWrap',
    hyphens: 'hyphens',
    whiteSpace: 'whiteSpace',
    wordBreak: 'wordBreak',
    overflow: 'overflow',
    fontStyle: 'fontStyle',
  };

export const useSapphireTextStyleProps = (
  props: SapphireTextStyleProps
): {
  styleProps: {
    style: React.CSSProperties;
    className?: string;
  };
  filteredProps: FilteredProps<
    SapphireTextStyleProps,
    SapphireCommonStyleProps
  >;
} =>
  useSapphireStyleProps<SapphireTextStyleProps>(
    props,
    sapphireTextStyleHandlers
  );
